import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  // Carrega o usuário ao iniciar
  useEffect(() => {
    api.get('/user/me')
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Carrega favoritos sempre que o usuário muda
  useEffect(() => {
    if (!user) { setFavoriteIds(new Set()); return; }
    api.get('/user/favorites')
      .then(r => setFavoriteIds(new Set((r.data || []).map(f => Number(f.id)))))
      .catch(() => {});
  }, [user]);

  async function login(email, password) {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data.user);
    return res.data.user;
  }

  async function loginWithGoogle(credential) {
    const res = await api.post('/auth/google', { credential });
    setUser(res.data.user);
    return res.data.user;
  }

  async function logout() {
    await api.post('/auth/logout').catch(() => {});
    setUser(null);
    setFavoriteIds(new Set());
  }

  // Toggle favorite — estado global, persiste durante navegação
  const toggleFavorite = useCallback(async (rawPostId) => {
    const postId = Number(rawPostId);
    if (!postId) throw new Error('postId inválido');

    // Optimistic update
    const wasAlready = favoriteIds.has(postId);
    setFavoriteIds(prev => {
      const next = new Set(prev);
      wasAlready ? next.delete(postId) : next.add(postId);
      return next;
    });

    try {
      const { data } = await api.post('/user/toggle-favorite', { postId });
      // Sincroniza com o servidor
      setFavoriteIds(prev => {
        const next = new Set(prev);
        data.isFavorite ? next.add(postId) : next.delete(postId);
        return next;
      });
      return data.isFavorite;
    } catch (err) {
      // Reverte se falhou
      setFavoriteIds(prev => {
        const next = new Set(prev);
        wasAlready ? next.add(postId) : next.delete(postId);
        return next;
      });
      throw err;
    }
  }, [favoriteIds]);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, setUser, favoriteIds, toggleFavorite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
